import { useMemo, useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { ElectronicCardCardLinkOptionFragment } from '@graphql/generated';
import { SelectOptionFields } from '@shared/components/EmailsAndEcards/components/Design/Design.controller';

/**
 * Hook that provides translated labels for eCard link options.
 * Returns convenience functions to translate labels and optionally a transformed array of select options.
 */
export const useEcardLinkOptions = (linkOptions?: readonly ElectronicCardCardLinkOptionFragment[]) => {
  const { tq } = useTranslation('emailsAndEcards');

  // prettier-ignore
  const labelMap = useMemo(() => ({
    calendar:       tq`Add to Calendar`,
    rsvp:           tq`RSVP`,
    registry:       tq`Browse Registry`,
    schedule:       tq`View Schedule`,
    travel:         tq`View Travel Details`,
    website:        tq`View Website`,
    mailingAddress: tq`Enter Mailing Address`,
  }), [tq]) as Record<string, string>;

  // Symmetry between input and output
  type TranslateLinkOption = {
    (option: undefined): undefined;
    (option: SelectOptionFields): SelectOptionFields;
    (option: SelectOptionFields | undefined): SelectOptionFields | undefined;
  };

  const translateLinkOption = useCallback(
    (option: SelectOptionFields | undefined): SelectOptionFields | undefined => {
      if (!option) return undefined;
      return {
        ...option,
        label: labelMap[option.value] ?? option.label,
      };
    },
    [labelMap],
  ) as TranslateLinkOption;

  const ecardLinkOptions = useMemo(() => {
    if (!linkOptions) return [];
    return [
      {
        label: tq`None`,
        value: '',
      },
      ...linkOptions.map(item => ({
        label: labelMap[item.name] ?? item.label,
        value: item.name,
      })),
    ];
  }, [linkOptions, labelMap, tq]);

  return {
    labelMap,
    translateLinkOption,
    ecardLinkOptions,
  };
};
